import React, { useState, useEffect, useCallback } from 'react';
import './StudentAssignmentHistory.css';
import collegeConfig from '../../config/collegeConfig';
import { FaUserGraduate, FaHistory, FaCheckCircle } from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function StudentAssignmentHistory() {
  const [current, setCurrent] = useState([]);
  const [previous, setPrevious] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [batchLabels, setBatchLabels] = useState({});

  useEffect(() => {
    fetch(`${API}/system-settings`)
      .then(r => r.json())
      .then(d => {
        if (d.success && Array.isArray(d.batchLabels)) {
          setBatchLabels(Object.fromEntries(d.batchLabels.map(b => [b.batch, b.label])));
        }
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/teachers/faculty-history/my-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setCurrent(data.current || []);
        setPrevious(data.previous || []);
      } else {
        setError(data.message || 'Failed to load student history');
      }
    } catch (err) {
      setError('Failed to load student history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="sah-container">
      <div className="sah-header">
        <h2 className="sah-page-title">Student Assignment History</h2>
      </div>

      {loading && <div className="sah-loading">Loading...</div>}
      {error && <div className="sah-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="sah-card">
            <h3 className="sah-card-title"><FaCheckCircle className="sah-icon-current" /> Current Students</h3>
            {current.length === 0 ? (
              <p className="sah-empty">No students currently assigned to you.</p>
            ) : (
              <div className="sah-list">
                {current.map((entry) => (
                  <div className="sah-item sah-item-current" key={entry._id}>
                    <div className="sah-item-icon"><FaUserGraduate /></div>
                    <div className="sah-item-body">
                      <div className="sah-item-name">
                        {entry.studentName || 'Student'}
                        {entry.batch && (
                          <span className="sah-batch-tag">{batchLabels[entry.batch] || entry.batch}</span>
                        )}
                      </div>
                      <div className="sah-item-meta">{entry.courseName || 'Department'}</div>
                      <div className="sah-item-date">Assigned since {fmt(entry.assignedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sah-card">
            <h3 className="sah-card-title"><FaHistory className="sah-icon-previous" /> Previous Students</h3>
            {previous.length === 0 ? (
              <p className="sah-empty">No previous student assignments.</p>
            ) : (
              <div className="sah-list">
                {previous.map((entry) => (
                  <div className="sah-item" key={entry._id}>
                    <div className="sah-item-icon sah-item-icon-past"><FaUserGraduate /></div>
                    <div className="sah-item-body">
                      <div className="sah-item-name">
                        {entry.studentName || 'Student'}
                        {entry.batch && (
                          <span className="sah-batch-tag">{batchLabels[entry.batch] || entry.batch}</span>
                        )}
                      </div>
                      <div className="sah-item-meta">{entry.courseName || 'Department'}</div>
                      <div className="sah-item-date">{fmt(entry.assignedAt)} – {fmt(entry.endedAt)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
